import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Map as MapIcon, Handshake, DollarSign, TrendingUp, Download, Pencil, X, FileText, Link2 as LinkIcon, Trash2, Plus, Upload, RefreshCw, Sparkles, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { OVERLINE, H1, KpiCard, StatusBadge, Panel, btnPrimary, money, useAdmin } from "@/components/admin/ui";
import MediaPicker from "@/components/cms/MediaPicker";

const CATEGORY_LABELS = {
  draw_odds: "Draw odds", point_creep: "Point creep", regulations: "Regulations",
  seasons: "Seasons", licenses: "Licenses", temporary: "Temporary / Emergency", general: "General",
};
const DOC_STATUSES = ["current", "historical", "superseded", "archived"];

const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";
const Field = ({ label, children }) => (<label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{label}</span>{children}</label>);
const label = (s) => (s || "not_contacted").replace(/_/g, " ");

function AiStatusPill({ p }) {
  const st = (p || {}).status || "pending";
  const map = {
    ready: { c: "bg-[#2E7D46]/15 text-[#2E7D46]", i: CheckCircle2, t: "Ready for AI" },
    needs_review: { c: "bg-[#B8912E]/15 text-[#8A6D1F]", i: AlertTriangle, t: "Needs review" },
    processing: { c: "bg-[#B8912E]/15 text-[#8A6D1F]", i: Loader2, t: "Processing…" },
    pending: { c: "bg-[#E5E2DC] text-[#5C6B61]", i: Clock, t: "Pending" },
    failed: { c: "bg-[#B04A3A]/15 text-[#B04A3A]", i: AlertTriangle, t: "Failed" },
  };
  const m = map[st] || map.pending;
  const Icon = m.i;
  const extra = (st === "ready" || st === "needs_review") && p?.pages_total ? ` · ${p.pages_indexed}/${p.pages_total} pages` : "";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${m.c}`} title={p?.review_reason || p?.error || m.t} data-testid="doc-ai-status">
      <Icon size={11} className={st === "processing" ? "animate-spin" : ""} /> {m.t}{extra}
    </span>
  );
}

function StateDocsManager({ state, canEdit }) {
  const [docs, setDocs] = useState(null);
  const [pick, setPick] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [reproc, setReproc] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [srcTest, setSrcTest] = useState({ id: null, query: "", busy: false, result: null });
  const [showTest, setShowTest] = useState(false);
  const [testQ, setTestQ] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [errId, setErrId] = useState(null);
  const blank = { title: "", category: "regulations", doc_type: "link", url: "", source: "", source_date: "", regulation_year: "", official_source: true, status: "current" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  const load = useCallback(() => {
    api.adminStateDocs(state).then(setDocs).catch(() => setDocs([]));
    api.adminStateAiStatus(state).then(setAiStatus).catch(() => setAiStatus(null));
  }, [state]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.title.trim() || !f.url.trim()) { toast.error("Title and a file/link are required"); return; }
    setBusy(true);
    try { await api.adminStateDocCreate(state, f); toast.success("Document added — indexing for AI…"); setF(blank); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this document? Its AI index will be removed too.")) return; try { await api.adminStateDocDelete(state, id); load(); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const process = async (id) => {
    setProcessingId(id);
    try {
      await api.adminStateDocProcess(state, id);
      toast.message("Indexing started — this runs in the background…");
      load();
      // Poll this source's status until it reaches a terminal state.
      if (pollRef.current) clearInterval(pollRef.current);
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const list = await api.adminStateDocs(state);
          setDocs(list);
          api.adminStateAiStatus(state).then(setAiStatus).catch(() => {});
          const d = (list || []).find((x) => x.id === id);
          const st = d?.processing?.status;
          if (st === "ready" || st === "needs_review") {
            clearInterval(poll); setProcessingId(null);
            toast.success(st === "ready" ? "Indexed for AI" : "Indexed — needs review");
          } else if (st === "failed") {
            clearInterval(poll); setProcessingId(null);
            toast.error(d?.processing?.error || "Indexing failed — see View error");
          } else if (Date.now() - started > 360000) {
            clearInterval(poll); setProcessingId(null);
            toast.message("Still indexing — check back shortly");
          }
        } catch { clearInterval(poll); setProcessingId(null); }
      }, 2500);
      pollRef.current = poll;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start indexing");
      setProcessingId(null);
    }
  };
  const reprocessAll = async () => {
    if (!window.confirm("Re-index every document in every state's profile for HomeRange AI? This runs in the background.")) return;
    setReproc(true);
    try {
      const r = await api.adminReprocessAllKb();
      if (r.already_running) toast.message("Indexing is already running…");
      else toast.message(`Indexing ${r.total} document${r.total === 1 ? "" : "s"} in the background…`);
      const poll = setInterval(async () => {
        try {
          const j = await api.adminKbJob();
          if (j.status === "running") {
            setReproc(`${j.done}/${j.total}`);
          } else if (j.status === "done") {
            clearInterval(poll);
            setReproc(false);
            toast.success(`Indexed ${j.ok}/${j.total} documents${j.failed ? `, ${j.failed} failed` : ""}`);
            load();
          } else {
            clearInterval(poll);
            setReproc(false);
          }
        } catch { clearInterval(poll); setReproc(false); }
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setReproc(false); }
  };
  const runTest = async () => {
    if (!testQ.trim()) return;
    setTestBusy(true); setTestRes(null);
    try { setTestRes(await api.adminStateAiTest(state, testQ.trim())); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); } finally { setTestBusy(false); }
  };
  const setDocStatus = async (id, status) => {
    try { await api.adminStateDocUpdate(state, id, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const runSrcTest = async (id) => {
    if (!srcTest.query.trim()) return;
    setSrcTest((s) => ({ ...s, busy: true, result: null }));
    try { const r = await api.adminSourceAiTest(state, id, srcTest.query.trim()); setSrcTest((s) => ({ ...s, busy: false, result: r })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); setSrcTest((s) => ({ ...s, busy: false })); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#E5E2DC]" data-testid="state-docs">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2"><FileText size={15} className="text-[#8A5A44]" /><h4 className="font-serif text-lg text-[#1F2A22]">Source documents & HomeRange AI knowledge</h4></div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTest((v) => !v)} className="inline-flex items-center gap-1 text-xs border border-[#1F2A22]/25 px-2.5 py-1.5 hover:bg-[#1F2A22] hover:text-white" data-testid="state-ai-test-toggle"><Sparkles size={12} /> Test HomeRange AI</button>
            <button onClick={reprocessAll} disabled={!!reproc} className="inline-flex items-center gap-1 text-xs border border-[#FF6A00] text-[#FF6A00] px-2.5 py-1.5 hover:bg-[#FF6A00] hover:text-white disabled:opacity-50" data-testid="state-reprocess-all">{reproc ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {typeof reproc === "string" ? `Indexing ${reproc}` : "Re-index all"}</button>
          </div>
        )}
      </div>
      <p className="text-xs text-[#5C6B61] mb-3">Official PDFs or links backing this state. Approved resources are read, indexed, and used by HomeRange AI with page-level citations.</p>

      {aiStatus && (
        <div className="flex flex-wrap gap-3 text-[11px] text-[#5C6B61] bg-[#F2F0EB] px-3 py-2 mb-3" data-testid="state-ai-summary">
          <span><b className="text-[#2E7D46]">{aiStatus.ready}</b> ready</span>
          <span><b className="text-[#8A6D1F]">{aiStatus.needs_review || 0}</b> needs review</span>
          <span><b className="text-[#8A6D1F]">{aiStatus.pending}</b> pending</span>
          <span><b className="text-[#B04A3A]">{aiStatus.failed}</b> failed</span>
          <span><b className="text-[#1F2A22]">{aiStatus.chunks_indexed}</b> searchable sections</span>
        </div>
      )}

      {showTest && (
        <div className="bg-white border border-[#1F2A22]/20 p-3 mb-4" data-testid="state-ai-test-panel">
          <div className="flex gap-2">
            <input value={testQ} onChange={(e) => setTestQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runTest()} className={inp} placeholder={`Ask ${state}'s HomeRange AI… e.g. "rifle elk season in Unit 50"`} data-testid="state-ai-test-input" />
            <button onClick={runTest} disabled={testBusy} className={`${btnPrimary} shrink-0`} data-testid="state-ai-test-run">{testBusy ? <Loader2 size={15} className="animate-spin" /> : "Ask"}</button>
          </div>
          {testRes && (
            <div className="mt-3 text-sm" data-testid="state-ai-test-result">
              <div className="whitespace-pre-wrap text-[#1F2A22] bg-[#F9F8F6] p-3 border border-[#E5E2DC] max-h-64 overflow-auto">{testRes.answer}</div>
              <div className="flex flex-wrap gap-3 text-[11px] text-[#8A8478] mt-2">
                <span>Retrieval {testRes.timings_ms?.retrieval}ms</span>
                <span>Generation {testRes.timings_ms?.generation}ms</span>
                <span>Total {testRes.timings_ms?.total}ms</span>
              </div>
              {testRes.diagnostics && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="state-ai-test-diagnostics">
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 bg-[#1F2A22]/8 text-[#1F2A22]">{testRes.diagnostics.engine}{testRes.diagnostics.fallback ? " (fallback)" : ""}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 bg-[#2E7D46]/12 text-[#2E7D46]">{testRes.diagnostics.doc_sections_retrieved} stored sections</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${testRes.diagnostics.web_used ? "bg-[#B8912E]/15 text-[#8A6D1F]" : "bg-[#E5E2DC] text-[#5C6B61]"}`}>{testRes.diagnostics.web_used ? `web research · ${testRes.diagnostics.web_sources_used} sources` : "no web research"}</span>
                  {testRes.diagnostics.web_queries?.length > 0 && (
                    <span className="text-[10px] text-[#8A8478] w-full mt-0.5">Searched: {testRes.diagnostics.web_queries.join(" · ")}</span>
                  )}
                </div>
              )}
              {testRes.sources?.length > 0 && (
                <div className="mt-2">
                  <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Sources used</span>
                  <ul className="mt-1 space-y-0.5">
                    {testRes.sources.map((s, i) => (
                      <li key={i} className="text-[11px] text-[#5C6B61]" data-testid={`test-source-${i}`}>• {s.title}{s.page ? ` — Page ${s.page}` : ""}{s.agency ? ` (${s.agency})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {docs === null ? <Loader2 className="animate-spin text-[#FF6A00]" /> : docs.length === 0 ? (
        <p className="text-xs text-[#8A8478] mb-3">No documents yet.</p>
      ) : (
        <div className="border border-[#E5E2DC] divide-y divide-[#E5E2DC] mb-4" data-testid="state-docs-list">
          {docs.map((d) => (
            <div key={d.id} data-testid={`state-doc-${d.id}`}>
              <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
                {d.doc_type === "pdf" ? <FileText size={14} className="text-[#8A5A44] shrink-0" /> : <LinkIcon size={14} className="text-[#5C6B61] shrink-0" />}
                <div className="flex-1 min-w-[180px]">
                  <a href={mediaSrc(d.url)} target="_blank" rel="noreferrer" className="text-sm text-[#1F2A22] truncate block hover:text-[#FF6A00]">{d.title}</a>
                  <p className="text-[11px] text-[#5C6B61] truncate">{CATEGORY_LABELS[d.category] || d.category}{d.regulation_year ? ` · ${d.regulation_year}` : ""}{d.source ? ` · ${d.source}` : ""}</p>
                </div>
                <AiStatusPill p={d.processing} />
                {(d.processing?.status === "failed" || d.processing?.status === "needs_review") && (
                  <button onClick={() => setErrId((x) => (x === d.id ? null : d.id))} className="p-1 text-[#B04A3A] hover:bg-red-50 shrink-0" title="View error / diagnostics" data-testid={`state-doc-viewerror-${d.id}`}><AlertTriangle size={13} /></button>
                )}
                {canEdit && (
                  <select value={d.status || "current"} onChange={(e) => setDocStatus(d.id, e.target.value)} className="text-[11px] border border-[#E5E2DC] bg-white px-1 py-1 shrink-0" data-testid={`state-doc-status-${d.id}`} title="Regulation status">
                    {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {canEdit && <button onClick={() => setSrcTest((s) => ({ id: s.id === d.id ? null : d.id, query: "", busy: false, result: null }))} className="p-1 text-[#5C6B61] hover:text-[#1F2A22] shrink-0" title="Test AI on this source" data-testid={`state-doc-test-${d.id}`}><Sparkles size={13} /></button>}
                {canEdit && <button onClick={() => process(d.id)} disabled={processingId === d.id} className="p-1 text-[#5C6B61] hover:text-[#FF6A00] shrink-0 disabled:opacity-40" title="Re-index this document" data-testid={`state-doc-retry-${d.id}`}>{processingId === d.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}</button>}
                {canEdit && <button onClick={() => remove(d.id)} className="p-1 text-red-600 hover:bg-red-50 shrink-0" title="Delete"><Trash2 size={13} /></button>}
              </div>
              {srcTest.id === d.id && (
                <div className="px-3 pb-3 bg-[#F9F8F6]" data-testid={`state-doc-test-panel-${d.id}`}>
                  <div className="flex gap-2">
                    <input value={srcTest.query} onChange={(e) => setSrcTest((s) => ({ ...s, query: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && runSrcTest(d.id)} className={inp} placeholder="Ask a question answered only by this document…" data-testid={`state-doc-test-input-${d.id}`} />
                    <button onClick={() => runSrcTest(d.id)} disabled={srcTest.busy} className={`${btnPrimary} shrink-0`} data-testid={`state-doc-test-run-${d.id}`}>{srcTest.busy ? <Loader2 size={15} className="animate-spin" /> : "Ask"}</button>
                  </div>
                  {srcTest.result && (
                    <div className="mt-2 text-sm whitespace-pre-wrap text-[#1F2A22] bg-white border border-[#E5E2DC] p-3 max-h-56 overflow-auto" data-testid={`state-doc-test-result-${d.id}`}>
                      {srcTest.result.answer}
                      <div className="text-[11px] text-[#8A8478] mt-2">{srcTest.result.chunk_count} sections · {srcTest.result.gen_ms}ms</div>
                    </div>
                  )}
                </div>
              )}
              {errId === d.id && (
                <div className="px-3 pb-3 bg-[#FBF4F2] border-t border-[#E7D3CD]" data-testid={`state-doc-error-panel-${d.id}`}>
                  <div className="pt-2 text-[12px] text-[#5C3A32] space-y-1">
                    <div><span className="font-semibold uppercase tracking-wide text-[10px]">Status:</span> {d.processing?.status || "pending"}</div>
                    {d.processing?.failed_stage && <div><span className="font-semibold uppercase tracking-wide text-[10px]">Failed stage:</span> {d.processing.failed_stage}</div>}
                    <div><span className="font-semibold uppercase tracking-wide text-[10px]">Reason:</span> {d.processing?.error || d.processing?.review_reason || "—"}</div>
                    {d.processing?.last_processed && <div><span className="font-semibold uppercase tracking-wide text-[10px]">Last attempt:</span> {new Date(d.processing.last_processed).toLocaleString()}</div>}
                  </div>
                  {canEdit && (
                    <button onClick={() => process(d.id)} disabled={processingId === d.id} className="mt-2 inline-flex items-center gap-1.5 text-xs bg-[#1F2A22] text-white px-2.5 py-1.5 disabled:opacity-40" data-testid={`state-doc-retry-inline-${d.id}`}>
                      {processingId === d.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Retry indexing (this source only)
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="space-y-2 bg-white border border-[#E5E2DC] p-3" data-testid="state-doc-form">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="Document title (e.g. 2026 Elk Regulations)" data-testid="state-doc-title" />
          <div className="grid grid-cols-2 gap-2">
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} data-testid="state-doc-category">
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value, url: "" })} className={inp} data-testid="state-doc-type">
              <option value="link">External link</option>
              <option value="pdf">PDF (from library)</option>
            </select>
          </div>
          {f.doc_type === "pdf" ? (
            <div className="flex items-center gap-2">
              <input value={f.url} readOnly className={inp} placeholder="Choose a PDF from the library" />
              <button onClick={() => setPick(true)} className="shrink-0 inline-flex items-center gap-1 text-xs border border-[#1F2A22]/25 px-2.5 py-2 hover:bg-[#1F2A22] hover:text-white" data-testid="state-doc-pick-pdf"><Upload size={12} /> Library</button>
            </div>
          ) : (
            <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className={inp} placeholder="https://official-agency.gov/…" data-testid="state-doc-url" />
          )}
          <div className="grid grid-cols-3 gap-2">
            <input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className={inp} placeholder="Source / agency" data-testid="state-doc-source" />
            <input value={f.regulation_year} onChange={(e) => setF({ ...f, regulation_year: e.target.value })} className={inp} placeholder="Reg. year (e.g. 2026)" data-testid="state-doc-year" />
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp} data-testid="state-doc-status">
              <option value="current">Current</option>
              <option value="historical">Historical</option>
              <option value="superseded">Superseded</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#5C6B61]">
            <input type="checkbox" checked={f.official_source !== false} onChange={(e) => setF({ ...f, official_source: e.target.checked })} data-testid="state-doc-official" />
            Official source (prioritized by HomeRange AI)
          </label>
          <button onClick={add} disabled={busy} className={`${btnPrimary} w-full`} data-testid="state-doc-add">{busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={14} /> Add document</>}</button>
        </div>
      )}
      <MediaPicker open={pick} kind="pdf" onClose={() => setPick(false)} onSelect={(url) => setF((prev) => ({ ...prev, url, doc_type: "pdf" }))} />
    </div>
  );
}

function StateModal({ state, statuses, canEdit, onClose, onSaved }) {
  const [f, setF] = useState({
    status: state.status || "not_contacted", agency: state.agency || "", partnership_tier: state.partnership_tier || "",
    contact_name: state.contact_name || "", contact_email: state.contact_email || "",
    contract_value: state.contract_value_cents ? state.contract_value_cents / 100 : "",
    start_date: state.start_date || "", renewal_date: state.renewal_date || "", notes: state.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.adminStateUpdate(state.state, {
        status: f.status, agency: f.agency || null, partnership_tier: f.partnership_tier || null,
        contact_name: f.contact_name || null, contact_email: f.contact_email || null,
        contract_value_cents: f.contract_value ? Math.round(Number(f.contract_value) * 100) : 0,
        start_date: f.start_date || null, renewal_date: f.renewal_date || null, notes: f.notes || null,
      });
      toast.success(`${state.state} updated`); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="state-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-[#F9F8F6] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC] bg-white sticky top-0">
          <h3 className="font-serif text-xl text-[#1F2A22]">{state.state}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="state-modal-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Pipeline stage">
            <select value={f.status} onChange={set("status")} disabled={!canEdit} className={inp} data-testid="state-status">
              {statuses.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field label="Agency"><input value={f.agency} onChange={set("agency")} disabled={!canEdit} className={inp} data-testid="state-agency" /></Field>
          <Field label="Partnership tier"><input value={f.partnership_tier} onChange={set("partnership_tier")} disabled={!canEdit} className={inp} placeholder="pilot / founding / standard" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact name"><input value={f.contact_name} onChange={set("contact_name")} disabled={!canEdit} className={inp} /></Field>
            <Field label="Contact email"><input value={f.contact_email} onChange={set("contact_email")} disabled={!canEdit} className={inp} /></Field>
          </div>
          <Field label="Contract value (USD / yr)"><input type="number" value={f.contract_value} onChange={set("contract_value")} disabled={!canEdit} className={inp} data-testid="state-contract-value" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><input type="date" value={f.start_date} onChange={set("start_date")} disabled={!canEdit} className={inp} /></Field>
            <Field label="Renewal date"><input type="date" value={f.renewal_date} onChange={set("renewal_date")} disabled={!canEdit} className={inp} /></Field>
          </div>
          <Field label="Notes"><textarea rows={3} value={f.notes} onChange={set("notes")} disabled={!canEdit} className={inp} /></Field>
          {canEdit && <button onClick={save} disabled={busy} className={`${btnPrimary} w-full`} data-testid="state-save">{busy ? <Loader2 size={15} className="animate-spin" /> : "Save state"}</button>}
          <StateDocsManager state={state.state} canEdit={canEdit} />
        </div>
      </div>
    </div>
  );
}

const LEVEL_STYLE = {
  strong: { c: "bg-[#2E7D46]/15 text-[#2E7D46]", bar: "bg-[#2E7D46]", t: "Strong" },
  good: { c: "bg-[#2E7D46]/12 text-[#2E7D46]", bar: "bg-[#5FA86E]", t: "Good" },
  partial: { c: "bg-[#B8912E]/15 text-[#8A6D1F]", bar: "bg-[#B8912E]", t: "Partial" },
  attention: { c: "bg-[#B04A3A]/15 text-[#B04A3A]", bar: "bg-[#B04A3A]", t: "Needs sources" },
  none: { c: "bg-[#E5E2DC] text-[#5C6B61]", bar: "bg-[#C9C4BA]", t: "No AI knowledge" },
};

function CoveragePanel({ onOpenState }) {
  const [cov, setCov] = useState(null);
  useEffect(() => { api.adminKbCoverage().then(setCov).catch(() => setCov({ states: [], summary: {} })); }, []);
  if (!cov) return <Panel title="HomeRange AI coverage" className="mt-6"><Loader2 className="animate-spin text-[#FF6A00]" /></Panel>;
  const sm = cov.summary || {};
  return (
    <Panel title="HomeRange AI coverage" className="mt-6" data-testid="ai-coverage-panel">
      <div className="flex flex-wrap gap-4 text-xs text-[#5C6B61] mb-4" data-testid="ai-coverage-summary">
        <span><b className="text-[#1F2A22] text-sm">{sm.answerable_states ?? 0}</b> answerable states</span>
        <span><b className="text-[#1F2A22] text-sm">{sm.states_with_sources ?? 0}</b> with sources</span>
        <span><b className="text-[#B04A3A] text-sm">{sm.states_with_failures ?? 0}</b> with failures</span>
        <span><b className="text-[#1F2A22] text-sm">{sm.total_sections ?? 0}</b> searchable sections</span>
      </div>
      {(!cov.states || cov.states.length === 0) ? (
        <p className="text-xs text-[#8A8478]">No state has source documents yet. Add PDFs/links to a state profile below to build HomeRange AI knowledge.</p>
      ) : (
        <div className="divide-y divide-[#E5E2DC] border border-[#E5E2DC]">
          {cov.states.map((r) => {
            const st = LEVEL_STYLE[r.level] || LEVEL_STYLE.none;
            return (
              <button key={r.state} onClick={() => onOpenState(r.state)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F9F8F6] transition-colors" data-testid={`ai-coverage-row-${r.state}`}>
                <span className="w-28 shrink-0 text-sm text-[#1F2A22] font-medium truncate">{r.state}</span>
                <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${st.c}`}>{st.t}</span>
                <div className="flex-1 min-w-[80px] h-2 bg-[#EEEBE5] rounded-full overflow-hidden">
                  <div className={`h-full ${st.bar}`} style={{ width: `${r.score}%` }} data-testid={`ai-coverage-bar-${r.state}`} />
                </div>
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-[#5C6B61]">{r.score}%</span>
                <span className="w-24 shrink-0 text-right text-[11px] text-[#8A8478]">{r.chunks} sec · {r.core_covered}/{r.core_total} cat</span>
                {r.failed > 0 && <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-[#B04A3A]" title={`${r.failed} failed source(s)`}><AlertTriangle size={11} />{r.failed}</span>}
              </button>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function SettingToggle({ label, hint, val, onFlip, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div><div className="text-sm text-[#1F2A22] font-medium">{label}</div><div className="text-[11px] text-[#8A8478]">{hint}</div></div>
      <button onClick={onFlip} disabled={disabled} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${val ? "bg-[#2E7D46]" : "bg-[#C9C4BA]"} disabled:opacity-50`} data-testid={testid}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${val ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}

function AiSettingsPanel({ canEdit }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.adminAiSettings().then(setS).catch(() => setS(null)); }, []);
  const save = async (patch) => {
    if (!canEdit) return;
    setSaving(true);
    try { setS(await api.adminUpdateAiSettings(patch)); toast.success("AI settings updated"); }
    catch { toast.error("Failed to update settings"); }
    finally { setSaving(false); }
  };
  if (!s) return null;
  return (
    <Panel title="HomeRange AI settings" className="mt-6" data-testid="ai-settings-panel">
      <div className="divide-y divide-[#E5E2DC]">
        <SettingToggle label="Customer HomeRange AI" hint="Master switch — turn off to show a maintenance message to customers" val={s.ai_enabled} onFlip={() => save({ ai_enabled: !s.ai_enabled })} disabled={!canEdit || saving} testid="ai-setting-enabled" />
        <SettingToggle label="Official web research" hint="Let Gemini search official sources when stored knowledge is thin" val={s.web_research} onFlip={() => save({ web_research: !s.web_research })} disabled={!canEdit || saving} testid="ai-setting-web" />
        <div className="flex items-center justify-between gap-4 py-2">
          <div><div className="text-sm text-[#1F2A22] font-medium">Answer model</div><div className="text-[11px] text-[#8A8478]">Primary engine (Claude is the automatic fallback)</div></div>
          <select value={s.model} onChange={(e) => save({ model: e.target.value })} disabled={!canEdit || saving} className="border border-[#E5E2DC] bg-white px-2 py-1.5 text-sm" data-testid="ai-setting-model">
            <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro (default — deepest)</option>
            <option value="gemini-3.8-flash">Gemini 3.8 Flash (latest, fastest)</option>
            <option value="gemini-3-flash-preview">Gemini 3 Flash (legacy fast)</option>
            <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
          </select>
        </div>
      </div>
    </Panel>
  );
}

export default function AdminStates() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("states");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.adminStates().then(setData).catch(() => setData({ states: [], summary: {}, statuses: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const blob = await api.adminStatesExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "state_partnerships.csv"; a.click();
      URL.revokeObjectURL(url); toast.success("Exported CSV");
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  const s = data.summary || {};
  const byStatus = s.by_status || {};
  const rows = filter ? data.states.filter((r) => (r.status || "not_contacted") === filter) : data.states;

  return (
    <div className="p-6 md:p-8" data-testid="admin-states">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Network</span><h1 className={`${H1} mt-1`}>State Partnerships</h1></div>
        <button onClick={exportCsv} className={btnPrimary} data-testid="states-export"><Download size={15} /> Export CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiCard label="States" value={s.total_states ?? 0} icon={MapIcon} testid="states-kpi-total" />
        <KpiCard label="Active partners" value={s.active_partners ?? 0} icon={Handshake} accent testid="states-kpi-active" />
        <KpiCard label="Pipeline value / yr" value={money(s.total_contract_value_cents ?? 0)} icon={DollarSign} testid="states-kpi-value" />
        <KpiCard label="Avg deal / yr" value={money(s.avg_contract_value_cents ?? 0)} icon={TrendingUp} testid="states-kpi-avg" />
      </div>

      {/* Pipeline by stage */}
      <Panel title="Pipeline by stage" className="mt-6">
        <div className="flex flex-wrap gap-2" data-testid="states-stage-summary">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 text-xs border ${filter === "" ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid="states-filter-all">All ({s.total_states ?? 0})</button>
          {(data.statuses || []).map((st) => (
            <button key={st} onClick={() => setFilter(st)} className={`px-3 py-1.5 text-xs border capitalize ${filter === st ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61] hover:border-[#1F2A22]"}`} data-testid={`states-filter-${st}`}>
              {label(st)} ({byStatus[st] || 0})
            </button>
          ))}
        </div>
      </Panel>

      <CoveragePanel onOpenState={(name) => { const row = data.states.find((x) => x.state === name); if (row) setEditing(row); }} />

      <AiSettingsPanel canEdit={canEdit} />

      <Panel className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E5E2DC]">
            <th className="px-4 py-2 font-serif">State</th><th className="px-4 py-2 font-serif">Stage</th><th className="px-4 py-2 font-serif">Agency</th><th className="px-4 py-2 font-serif">Tier</th><th className="px-4 py-2 font-serif">Contract / yr</th><th className="px-4 py-2 font-serif">Renewal</th><th className="px-4 py-2 font-serif text-right"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.state} className="border-b border-[#E5E2DC] last:border-0" data-testid={`state-row-${r.state}`}>
                <td className="px-4 py-3 text-sm text-[#1F2A22] font-medium">{r.state}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status || "not_contacted"} /></td>
                <td className="px-4 py-3 text-sm text-[#5C6B61]">{r.agency || "—"}</td>
                <td className="px-4 py-3 text-sm text-[#5C6B61] capitalize">{r.partnership_tier || "—"}</td>
                <td className="px-4 py-3 text-sm text-[#1F2A22] tabular-nums">{r.contract_value_cents ? money(r.contract_value_cents) : "—"}</td>
                <td className="px-4 py-3 text-sm text-[#5C6B61]">{r.renewal_date || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setEditing(r)} className="p-1.5 text-[#5C6B61] hover:text-[#1F2A22]" aria-label="Edit" data-testid={`state-edit-${r.state}`}><Pencil size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {editing && <StateModal state={editing} statuses={data.statuses || []} canEdit={canEdit} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
